import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../components/SystemComponent/table";
import { GroupButton } from "../../components/SystemComponent/GroupButton";
import { Badge } from "../../components/SystemComponent/Bagde";
import { Button } from "../../components/SystemComponent/Button";
import {
  MdPublic,
  MdFlag,
  MdLocationCity,
  MdFlight,
  MdAdd,
  MdEdit,
  MdDelete,
} from "react-icons/md";
import { createApi } from "../../../core/api";
import { LocationCrudModal } from "./Cruds/LocationCrudModal";

type Entity = "continent" | "country" | "city" | "airport";

const TABS = [
  {
    key: "continent",
    title: "Continents",
    icon: MdPublic,
    subTitle: "All continents",
  },
  {
    key: "country",
    title: "Countries",
    icon: MdFlag,
    subTitle: "All countries",
  },
  {
    key: "city",
    title: "Cities",
    icon: MdLocationCity,
    subTitle: "All cities",
  },
  {
    key: "airport",
    title: "Airports",
    icon: MdFlight,
    subTitle: "All airports",
  },
] as {
  key: Entity;
  title: string;
  icon: any;
  subTitle: string;
}[];

export const LocationManagement = () => {
  const api = useMemo(() => createApi(), []);

  const [entity, setEntity] = useState<Entity>("continent");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    continents: 0,
    countries: 0,
    cities: 0,
    airports: 0,
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const apiMap = useMemo(
    () => ({
      continent: {
        getAll: () => api.continent.getAll(),
        delete: (id: number) => api.continent.delete(id),
      },
      country: {
        getAll: () => api.country.getAll(),
        delete: (id: number) => api.country.delete(id),
      },
      city: {
        getAll: () => api.city.getAll(),
        delete: (id: number) => api.city.delete(id),
      },
      airport: {
        getAll: () => api.airport.getAll(),
        delete: (id: number) => api.airport.delete(id),
      },
    }),
    [api]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const r: any = await apiMap[entity].getAll();
    if (r?.ok) {
      const list = r.data?.data ?? r.data ?? [];
      setData(Array.isArray(list) ? list : []);
    }
    setLoading(false);
  }, [entity, apiMap]);

  const loadSummary = useCallback(async () => {
    const r: any = await api.location.summary();
    if (r?.ok) {
      setSummary(r.data?.data ?? r.data);
    }
  }, [api]);

  useEffect(() => {
    load();
    loadSummary();
  }, [entity, load, loadSummary]);

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setOpen(true);
  };

  const doDelete = async (id: number) => {
    setLoading(true);
    await apiMap[entity].delete(id);
    await load();
    await loadSummary();
    setLoading(false);
  };

  const columns = useMemo<any[]>(() => {
    const parentColumn =
      entity === "country"
        ? {
            key: "continent",
            title: "Continent",
            accessor: (r: any) => r.continent?.name,
            render: (r: any, v: string) => (
              <Badge icon={MdPublic} label={v ?? "-"} />
            ),
          }
        : entity === "city"
        ? {
            key: "country",
            title: "Country",
            accessor: (r: any) => r.country?.name,
            render: (r: any, v: string) => (
              <Badge icon={MdFlag} label={v ?? "-"} />
            ),
          }
        : entity === "airport"
        ? {
            key: "city",
            title: "City",
            accessor: (r: any) => r.city?.name,
            render: (r: any, v: string) => (
              <Badge icon={MdLocationCity} label={v ?? "-"} />
            ),
          }
        : null;

    return [
      ...(parentColumn ? [parentColumn] : []),
      {
        key: "name",
        title: "Name",
        minWidth: 260,
        accessor: (r: any) => r.name,
        render: (r: any, v: string) => (
          <Badge
            icon={
              entity === "continent"
                ? MdPublic
                : entity === "country"
                ? MdFlag
                : entity === "city"
                ? MdLocationCity
                : MdFlight
            }
            label={v}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 180,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                key: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                key: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => doDelete(row.id),
              },
            ]}
          />
        ),
      },
    ];
  }, [entity]);

  const tabMeta = TABS.find((x) => x.key === entity)!;

  return (
    <div style={S.wrap}>
      {loading && (
        <div style={S.loaderOverlay}>
          <div style={S.loader} />
        </div>
      )}

      <div style={S.tabs}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.key === entity;
          return (
            <div
              key={t.key}
              onClick={() => setEntity(t.key)}
              style={{ ...S.tab, ...(active ? S.tabActive : {}) }}
            >
              <Icon size={18} />
              {t.title}
            </div>
          );
        })}
      </div>

      <div style={S.summary}>
        <Badge icon={MdPublic} label="Continents" value={summary.continents} />
        <Badge icon={MdFlag} label="Countries" value={summary.countries} />
        <Badge icon={MdLocationCity} label="Cities" value={summary.cities} />
        <Badge icon={MdFlight} label="Airports" value={summary.airports} />
      </div>

      <div style={S.header}>
        <div>
          <div style={S.title}>{tabMeta.title}</div>
          <div style={S.subTitle}>{tabMeta.subTitle}</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Create
        </Button>
      </div>

      <Table
        tableKey={`location-${entity}`}
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      {open && (
        <LocationCrudModal
          open
          type={entity}
          mode={editing ? "edit" : "create"}
          data={editing}
          onClose={() => setOpen(false)}
          onSaved={() => {
            load();
            loadSummary();
          }}
        />
      )}
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  tabs: {
    display: "flex",
    gap: 10,
    marginBottom: 12,
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    borderRadius: 12,
    background: "#E2E8F0",
    cursor: "pointer",
    fontWeight: 600,
    color: "#334155",
  },
  tabActive: {
    background: "#2563EB",
    color: "#fff",
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: 12,
    marginBottom: 20,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  loaderOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(248,250,252,.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  loader: {
    width: 48,
    height: 48,
    border: "4px solid #CBD5E1",
    borderTopColor: "#2563EB",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};
